import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Tree, Empty, Spin, Button, message } from 'antd';
import {
  SearchOutlined,
  FolderOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { DataNode } from 'antd/es/tree';
import { getOntologyTree, type OntologyNode } from '../../../../api';

/** 选中态：仅 ONTOLOGY 类型可选；id 为后端 ontologyId */
export interface OntologySelection {
  /** 真实本体 id，对应后端 ontologyId 字段 */
  ontologyId: number;
  /** 展示名称 */
  name: string;
  /** 面包屑路径（不含自身） */
  pathNames: string[];
}

interface Props {
  open: boolean;
  /** 当前已选中的 ontologyId，用于高亮 */
  value?: number;
  onCancel: () => void;
  onOk: (selection: OntologySelection) => void;
}

/**
 * 把后端 OntologyNode[] 转成 antd Tree 使用的 DataNode[]
 * - DIRECTORY 节点：不可选（selectable=false）
 * - ONTOLOGY 节点：可选，key = ont-<ontologyId>
 * - 同时把原始节点挂到 DataNode 上，便于选中后取值
 */
interface TreeNode extends DataNode {
  raw: OntologyNode;
  pathNames: string[];
  children?: TreeNode[];
}

const DIR_KEY_PREFIX = 'dir-';
const ONT_KEY_PREFIX = 'ont-';

function buildTree(nodes: OntologyNode[], pathNames: string[]): TreeNode[] {
  return nodes.map(node => {
    const isOntology = node.type === 'ONTOLOGY';
    const key = isOntology
      ? `${ONT_KEY_PREFIX}${node.ontologyId}`
      : `${DIR_KEY_PREFIX}${node.id}`;
    const children = node.children?.length
      ? buildTree(node.children, [...pathNames, node.name])
      : undefined;

    const title = (
      <span className="inline-flex items-center gap-1.5">
        {isOntology ? (
          <FileTextOutlined className="text-[#2094f0]" />
        ) : (
          <FolderOutlined className="text-[#f2c037]" />
        )}
        <span className={isOntology ? 'text-[#111827]' : 'text-[#6b7280]'}>
          {node.name}
        </span>
        {isOntology && (
          <span className="text-xs text-[#9ca3af]">#{node.ontologyId}</span>
        )}
      </span>
    );

    return {
      key,
      title,
      selectable: isOntology,
      disabled: !isOntology && !children?.length,
      raw: node,
      pathNames,
      children,
    } as TreeNode;
  });
}

/** 收集所有目录节点的 key（用于默认展开/全展开） */
function collectAllKeys(nodes: TreeNode[]): React.Key[] {
  const keys: React.Key[] = [];
  const walk = (ns: TreeNode[]) => {
    ns.forEach(n => {
      keys.push(n.key);
      if (n.children?.length) walk(n.children);
    });
  };
  walk(nodes);
  return keys;
}

/**
 * 过滤：保留 name 命中关键字的节点，以及其所有祖先
 * 返回：过滤后的新树 + 需要展开的 key 列表
 */
function filterTree(
  nodes: TreeNode[],
  keyword: string
): { filtered: TreeNode[]; expandKeys: React.Key[] } {
  const kw = keyword.trim().toLowerCase();
  if (!kw) {
    return { filtered: nodes, expandKeys: [] };
  }

  const expandKeys: React.Key[] = [];

  const walk = (ns: TreeNode[]): TreeNode[] => {
    const result: TreeNode[] = [];
    ns.forEach(n => {
      const nameHit = n.raw.name.toLowerCase().includes(kw);
      const children = n.children ? walk(n.children) : undefined;
      if (nameHit || (children && children.length)) {
        if (children && children.length) expandKeys.push(n.key);
        // 保持命中节点展示，同时保留过滤后的子树
        result.push({
          ...n,
          children: children && children.length ? children : undefined,
        });
      }
    });
    return result;
  };

  return { filtered: walk(nodes), expandKeys };
}

const OntologyPickerModal: React.FC<Props> = ({
  open,
  value,
  onCancel,
  onOk,
}) => {
  const [keyword, setKeyword] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  const {
    data: treeData = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ontology-tree'],
    queryFn: getOntologyTree,
    enabled: open,
  });

  /** 构建 antd Tree 所需结构 */
  const fullTree = useMemo<TreeNode[]>(
    () => buildTree(treeData, []),
    [treeData]
  );

  /** 搜索过滤后的树 + 自动展开 key */
  const { filtered: visibleTree, expandKeys: searchExpandKeys } = useMemo(
    () => filterTree(fullTree, keyword),
    [fullTree, keyword]
  );

  /** 弹窗打开或树变化时，初始化选中态和展开态 */
  useEffect(() => {
    if (!open) return;
    // 根据传入 value 匹配已选节点
    if (value != null) {
      const targetKey = `${ONT_KEY_PREFIX}${value}`;
      setSelectedKey(targetKey);
      // 查找节点实例与其祖先路径，便于展开
      const walk = (
        ns: TreeNode[],
        ancestors: React.Key[]
      ): { node: TreeNode; ancestors: React.Key[] } | null => {
        for (const n of ns) {
          if (n.key === targetKey) return { node: n, ancestors };
          if (n.children?.length) {
            const found = walk(n.children, [...ancestors, n.key]);
            if (found) return found;
          }
        }
        return null;
      };
      const found = walk(fullTree, []);
      if (found) {
        setSelectedNode(found.node);
        setExpandedKeys(prev =>
          Array.from(new Set([...prev, ...found.ancestors]))
        );
      } else {
        setSelectedNode(null);
      }
    } else {
      setSelectedKey(null);
      setSelectedNode(null);
      // 默认展开一级目录
      setExpandedKeys(fullTree.map(n => n.key));
    }
    setAutoExpandParent(true);
  }, [open, value, fullTree]);

  /** 搜索关键字变化：自动展开命中分支 */
  useEffect(() => {
    if (!keyword.trim()) return;
    setExpandedKeys(prev =>
      Array.from(new Set([...prev, ...searchExpandKeys]))
    );
    setAutoExpandParent(true);
  }, [keyword, searchExpandKeys]);

  const handleSelect = (keys: React.Key[], info: { node: TreeNode }) => {
    const node = info.node;
    if (node.raw.type !== 'ONTOLOGY') return;
    setSelectedKey(String(node.key));
    setSelectedNode(node);
  };

  const handleConfirm = () => {
    if (!selectedNode || selectedNode.raw.type !== 'ONTOLOGY') {
      messageApi.warning('请选择一个本体');
      return;
    }
    const raw = selectedNode.raw;
    if (raw.ontologyId == null) {
      messageApi.error('该本体缺少 ontologyId，无法选择');
      return;
    }
    onOk({
      ontologyId: raw.ontologyId,
      name: raw.name,
      pathNames: selectedNode.pathNames,
    });
  };

  /** 渲染路径面包屑 */
  const breadcrumb = selectedNode ? (
    <span className="text-xs text-[#6b7280]">
      {selectedNode.pathNames.length > 0
        ? selectedNode.pathNames.join(' / ') + ' / '
        : ''}
      <span className="font-medium text-[#111827]">
        {selectedNode.raw.name}
      </span>
      <span className="ml-1 text-[#9ca3af]">
        (ontologyId: {selectedNode.raw.ontologyId})
      </span>
    </span>
  ) : (
    <span className="text-xs text-[#9ca3af]">尚未选择</span>
  );

  return (
    <Modal
      title="选择本体"
      open={open}
      onCancel={onCancel}
      width={640}
      destroyOnClose
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0 truncate text-left">{breadcrumb}</div>
          <div className="shrink-0 space-x-2">
            <Button onClick={onCancel}>取消</Button>
            <Button type="primary" onClick={handleConfirm}>
              确定
            </Button>
          </div>
        </div>
      }
    >
      {contextHolder}
      <div className="flex flex-col gap-3">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-[#bfbfbf]" />}
          placeholder="搜索本体名称"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
        />

        <div className="h-[420px] overflow-auto rounded-md border border-[#e5e7eb] bg-white p-2">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spin />
            </div>
          ) : isError ? (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <span className="text-sm text-[#ef4444]">
                加载失败：{(error as Error)?.message || '未知错误'}
              </span>
              <Button size="small" onClick={() => refetch()}>
                重试
              </Button>
            </div>
          ) : visibleTree.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={keyword ? '未搜到匹配的本体' : '暂无本体'}
              />
            </div>
          ) : (
            <Tree
              blockNode
              showLine={{ showLeafIcon: false }}
              treeData={visibleTree}
              expandedKeys={expandedKeys}
              autoExpandParent={autoExpandParent}
              onExpand={keys => {
                setExpandedKeys(keys);
                setAutoExpandParent(false);
              }}
              selectedKeys={selectedKey ? [selectedKey] : []}
              onSelect={(keys, info) =>
                handleSelect(keys, info as unknown as { node: TreeNode })
              }
              // 双击目录时展开/收起
              onDoubleClick={(_e, node) => {
                const n = node as unknown as TreeNode;
                if (n.raw.type !== 'DIRECTORY') return;
                setExpandedKeys(prev =>
                  prev.includes(n.key)
                    ? prev.filter(k => k !== n.key)
                    : [...prev, n.key]
                );
                setAutoExpandParent(false);
              }}
            />
          )}
        </div>

        <div className="text-xs text-[#9ca3af]">
          仅本体（
          <FileTextOutlined className="mx-0.5 text-[#2094f0]" />
          ）可被选中；目录（
          <FolderOutlined className="mx-0.5 text-[#f2c037]" />
          ）仅用于分组
        </div>
      </div>
    </Modal>
  );
};

export default OntologyPickerModal;
